import { SplitNumberInput } from '@components'

import { Color, Flex, Space, Spacing, Text } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_INVOICE_DISCOUNT_PERCENT } from '@features/invoices/constants/maxInvoiceDiscountPercent'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { getDiscountTypeTrackingValue } from '@features/invoices/utils/getDiscountTypeTrackingValue'
import { getInvoiceDiscountTypeSelectItems } from '@features/invoices/utils/getInvoiceDiscountTypeSelectItems'
import { InvoiceDiscountCalculator } from '@features/invoices/utils/invoiceDiscountCalculator'
import { isAmountDiscountType } from '@features/invoices/utils/isAmountDiscountType'
import { isPercentageDiscountType } from '@features/invoices/utils/isPercentageDiscountType'
import { useGetZervantDocumentSettings } from '@features/invoicingSettings/hooks/useGetZervantDocumentSettings'

import { useInvoiceEditorForm } from '../../../../../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useUpdateDependentLineValues } from '../../../../../../../../../../hooks/useUpdateDependentLineValues'
import type { CommonInvoiceLineFieldProps } from '../../../../../../types/commonInvoiceLineFieldProps'
import * as Styled from './styles'

export const InvoiceLineDiscount = ({ disabled = false, lineIndex }: CommonInvoiceLineFieldProps) => {
  const { t } = useTranslation()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { handleDiscountChange, handleDiscountTypeChange } = useUpdateDependentLineValues()
  const { getInvoiceLocale } = useGetZervantDocumentSettings(InvoiceDocumentType.Invoice)

  const { FormItem, setValue, watch } = invoiceEditorForm
  const locale = getInvoiceLocale()

  const [currency, discount, discountType, quantity, unitPrice, vatRatePercent] = watch([
    'currency',
    `invoiceLines.${lineIndex}.discount`,
    'discountType',
    `invoiceLines.${lineIndex}.quantity`,
    `invoiceLines.${lineIndex}.unitPrice`,
    `invoiceLines.${lineIndex}.vatRatePercent`,
  ])

  const discountTypeSelectItems = useMemo(() => getInvoiceDiscountTypeSelectItems(currency), [currency])

  const isPercentageDiscount = isPercentageDiscountType(discountType)
  const isAmountDiscount = isAmountDiscountType(discountType)
  const hasDiscountError = useMemo(() => {
    const { validationIssues } = InvoiceDiscountCalculator.calculateLine(
      { discount, quantity, unitPrice, vatRatePercent },
      discountType,
      currency,
    )

    return InvoiceDiscountCalculator.hasDiscountExceedsBaseIssue(validationIssues)
  }, [currency, discount, discountType, quantity, unitPrice, vatRatePercent])

  return (
    <Flex flexDirection="column" gap={Spacing.S4}>
      <FormItem
        name={`invoiceLines.${lineIndex}.discount`}
        render={({ field }) => (
          <SplitNumberInput
            key={discountType}
            {...field}
            alignment="left"
            disabled={disabled}
            dropdownOptions={discountTypeSelectItems}
            error={hasDiscountError}
            locale={locale}
            max={isPercentageDiscount ? MAX_INVOICE_DISCOUNT_PERCENT : undefined}
            min={0}
            onChange={(discountValue) => {
              if (discountValue === undefined) {
                return
              }
              handleDiscountChange(lineIndex, discountValue)
            }}
            onOptionChange={(type) => {
              handleDiscountTypeChange(type)
              setValue('discountType', type)
              trackInvoiceEditor('Form - Discount type changed', {
                discountType: getDiscountTypeTrackingValue(type),
              })
            }}
            selectedOption={discountType}
            size="sm"
            withFormatting={isAmountDiscount}
          />
        )}
      />
      {hasDiscountError && (
        <>
          <Space size="s6" />
          <Styled.DiscountError>
            <Text color={Color.Red700} variant="bodySM">
              {t('external_invoices.editor.form.field.line.field.discount.error')}
            </Text>
          </Styled.DiscountError>
        </>
      )}
    </Flex>
  )
}
